import { useState } from "react";

import { Loading } from "@smart/itops-sb-design-system-dom";

import { useOrderedSections, Visibility } from "../../hooks";
import {
  AutoFillStatus,
  FieldItem,
  GroupItem,
  ResponseSubmitter,
  SectionItem,
  SubmissionItem,
  TeamItem,
  Toast,
} from "../../types";
import { IntakeSummary } from "../summary";

export type IntakeFormSummaryProps = {
  sections: SectionItem[];
  groups: GroupItem[];
  fields: FieldItem[];
  onSelect: (section: SectionItem) => void;
  submitResponses: ResponseSubmitter;
  setStatus: (status: SubmissionItem["status"]) => void;
  team: TeamItem;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  responses: Record<string, any>;
  isLoadingDBResponses: boolean;
  visibility: Visibility;
  disabled?: boolean;
  previewOptions?: {
    previewResponses: Record<string, any>;
    clearLastPreviewSection: () => void;
    previewErrorToggle?: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
  };
  autoFillingStatus?: AutoFillStatus;
  isPreview?: boolean;
  isTeamHidden?: boolean;
  setToasts?: (toasts: Toast[]) => void;
};

export const IntakeFormSummary = ({
  sections,
  groups,
  fields,
  onSelect,
  submitResponses,
  setStatus,
  team,
  sidebar,
  setSidebar,
  responses,
  isLoadingDBResponses,
  visibility,
  disabled,
  previewOptions,
  autoFillingStatus,
  isPreview,
  isTeamHidden,
  setToasts,
}: IntakeFormSummaryProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const orderedSections = useOrderedSections({
    sections,
    groups,
    fields,
    visibility,
    responses,
  });
  const onSubmit = () => {
    if (previewOptions) previewOptions.clearLastPreviewSection();
    const status = "completed";
    setIsSubmitting(true);
    submitResponses({ status, responses: {} })
      .then((result) => {
        setIsSubmitting(false);
        if (result?.error) {
          setToasts?.([
            {
              text: result.error,
              palette: "destructive",
            },
          ]);
        } else if (result?.operationIds) setStatus(status);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      {isSubmitting && <Loading />}
      <IntakeSummary
        isForm={!disabled}
        hasPayment={Object.values(responses).some((r) => !!r?.chargeRequest)}
        team={team}
        onSelect={onSelect}
        onSubmit={onSubmit}
        onSubmitButton={disabled ? onSubmit : undefined}
        visibleSections={orderedSections}
        sidebar={sidebar}
        setSidebar={setSidebar}
        responses={previewOptions?.previewResponses || responses}
        isLoadingDBResponses={isLoadingDBResponses}
        previewErrorToggle={previewOptions?.previewErrorToggle}
        autoFillingStatus={autoFillingStatus}
        isPreview={isPreview}
        isTeamHidden={isTeamHidden}
        setToasts={setToasts}
      />
    </>
  );
};
